import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { FormProvider, useFieldArray, useForm } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import BWButton from 'components/shared/BWButton';
import FormItem from 'components/shared/BWFormControl/FormItem';
import FormInput from 'components/shared/BWFormControl/FormInput';
import FormTextArea from 'components/shared/BWFormControl/FormTextArea';
import FormNumber from 'components/shared/BWFormControl/FormNumber';
import FormSelect from 'components/shared/BWFormControl/FormSelect';
import FormDebouneSelect from 'components/shared/BWFormControl/FormDebouneSelect';
import { createCombo, getComboDetail, getComboProductOptions, getComboTypes, updateCombo } from 'services/promotions.service';
import { showToast } from 'utils/helpers';
import { formatPrice } from 'utils';

const Section = styled.div`
  margin-bottom: 18px;
  padding: 22px 24px;
  border: 1px solid #eef2f6;
  border-radius: 14px;
  background: #fff;
  box-shadow: none;
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 18px;
  padding-bottom: 14px;
  border-bottom: 1px solid #edf2f7;

  h3 {
    margin: 0;
    color: #101828;
    font-size: 18px;
    font-weight: 700;
  }
`;

const ComboLayout = styled.div`
  display: grid;
  grid-template-columns: minmax(390px, 0.62fr) minmax(720px, 1.38fr);
  gap: 18px;
  align-items: flex-start;

  @media (max-width: 1750px) {
    grid-template-columns: minmax(0, 1fr);
  }
`;

const InfoGrid = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  gap: 14px;

  @media (max-width: 1750px) and (min-width: 769px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));

    > :last-child {
      grid-column: 1 / -1;
    }
  }

  .bw_combo_code_input {
    font-family: Inter, Arial, sans-serif;
    font-weight: 700;
    letter-spacing: 0.02em;
    text-transform: uppercase;
  }
`;

const ProductToolbar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 12px;
  flex-wrap: wrap;
`;

const ProductStats = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
`;

const StatPill = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  min-height: 32px;
  padding: 6px 10px;
  border: 1px solid #e4e7ec;
  border-radius: 8px;
  background: #f8fafc;
  color: #344054;
  font-size: 12px;
  font-weight: 700;

  strong {
    color: #101828;
  }
`;

const PriceSummary = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 10px;
  margin-bottom: 16px;

  @media (max-width: 1100px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  @media (max-width: 480px) {
    grid-template-columns: minmax(0, 1fr);
  }
`;

const PriceCard = styled.div`
  padding: 12px 14px;
  border: 1px solid #edf2f7;
  border-radius: 10px;
  background: #f8fafc;

  span {
    display: block;
    color: #667085;
    font-size: 12px;
    font-weight: 700;
  }

  strong {
    display: block;
    margin-top: 4px;
    color: #101828;
    font-size: 18px;
    font-weight: 800;
    line-height: 1.25;
    white-space: nowrap;
  }

  small {
    display: block;
    margin-top: 4px;
    color: #667085;
    font-size: 11px;
    font-weight: 700;
  }
`;

const AppliedPriceCard = styled(PriceCard)`
  border-color: #b2ddff;
  background: linear-gradient(135deg, #eff8ff 0%, #f8fbff 100%);
`;

const QuickGuide = styled.div`
  margin-top: 14px;
  padding: 12px 14px;
  border: 1px solid #d1fadf;
  border-radius: 10px;
  background: #f6fef9;

  strong {
    display: block;
    margin-bottom: 6px;
    color: #05603a;
    font-size: 13px;
  }

  span {
    display: block;
    color: #475467;
    font-size: 12px;
    line-height: 1.55;
  }
`;

const PriceCell = styled.div`
  width: 100%;
  min-width: 0;

  small {
    display: block;
    margin-top: 4px;
    color: #667085;
    font-size: 11px;
    font-weight: 700;
    text-align: right;
    white-space: nowrap;
  }
`;

const ProductTable = styled.div`
  overflow: auto;
  border: 1px solid #e4e7ec;
  border-radius: 10px;

  table {
    width: 100%;
    min-width: 1040px;
    border-collapse: collapse;
    table-layout: fixed;
  }

  th {
    height: 40px;
    padding: 0 6px;
    background: #101828;
    color: #fff;
    font-size: 12px;
    font-weight: 700;
    text-align: left;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  td {
    padding: 8px 6px;
    border-top: 1px solid #edf2f7;
    vertical-align: top;
    font-size: 13px;
    overflow: hidden;
  }

  tfoot td {
    padding: 11px 8px;
    border-top: 2px solid #d0d5dd;
    background: #f8fafc;
    color: #101828;
    font-size: 13px;
    font-weight: 800;
    white-space: nowrap;
    vertical-align: middle;
  }

  .bw_combo_inline_control {
    display: flex;
    min-height: 34px;
    align-items: center;
  }

  .bw_combo_inline_control.bw_combo_inline_control_center {
    justify-content: center;
  }

  .bw_combo_gift_checkbox.bw_checkbox {
    width: 18px;
    height: 18px;
    min-width: 18px;
    margin: 0 !important;
    overflow: visible;
    justify-content: center;
  }

  .bw_combo_gift_checkbox.bw_checkbox span {
    width: 18px;
    height: 18px;
    min-width: 18px;
    flex: 0 0 18px;
    margin: 0 !important;
    border-radius: 4px;
  }

  tfoot td.bw_combo_total_label {
    text-align: right;
    text-transform: uppercase;
  }

  tfoot td.bw_combo_total_value {
    color: #027a48;
    text-align: right;
  }

  .bw_frm_box {
    margin: 0;
  }

  &&&& .ant-input-number.ant-input-number {
    min-height: 34px !important;
    height: 34px !important;
    max-height: 34px !important;
    padding: 0 !important;
    border-radius: 8px;
  }

  &&&& .ant-input-number-input-wrap,
  &&&& .ant-input-number-input {
    min-height: 32px !important;
    height: 32px !important;
    max-height: 32px !important;
  }

  &&&& .ant-input-number-input {
    padding: 0 6px !important;
    line-height: 32px !important;
    text-align: right;
    font-size: 12px;
  }

  &&&& input.bw_inp {
    min-height: 34px !important;
    height: 34px !important;
    max-height: 34px !important;
    padding: 0 10px !important;
    line-height: 32px !important;
    border-radius: 8px;
    font-size: 13px;
  }

  &&&& .ant-select .ant-select-selector {
    min-height: 34px !important;
    height: 34px !important;
    max-height: 34px !important;
    padding-top: 0 !important;
    padding-bottom: 0 !important;
  }

  .ant-select .ant-select-selection-item,
  .ant-select .ant-select-selection-placeholder {
    line-height: 32px !important;
  }

  .ant-select .ant-select-selection-search-input {
    min-height: 32px !important;
    height: 32px !important;
  }
`;

const EmptyProducts = styled.div`
  padding: 28px;
  color: #98a2b3;
  text-align: center;
`;

const ProductSubText = styled.div`
  margin-top: 4px;
  color: ${(props) => (props.$hasPrice ? '#027a48' : '#98a2b3')};
  font-size: 11px;
  font-weight: 700;
`;

const ProfitValue = styled.strong`
  color: ${(props) => (Number(props.$value || 0) < 0 ? '#d92d20' : '#027a48')} !important;
`;

const PageActions = styled.div`
  position: sticky;
  z-index: 5;
  bottom: 0;
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding: 14px 0 4px;
  background: linear-gradient(180deg, rgba(245, 247, 251, 0), #f5f7fb 34%);
`;

const defaultValues = {
  combo_name: '',
  combo_code: '',
  combo_type_id: null,
  url_combo: '',
  combo_list_price: 0,
  combo_price: 0,
  description: '',
  is_active: 1,
  products: [],
};

const moneyFormatter = (value) => {
  if (value === undefined || value === null || value === '') return '';
  const normalized = String(value).replace(/[^\d.-]/g, '');
  const [integerPart, decimalPart] = normalized.split('.');
  const formattedInteger = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  return decimalPart !== undefined ? `${formattedInteger},${decimalPart}` : formattedInteger;
};

const moneyParser = (value) => {
  if (value === undefined || value === null || value === '') return '';
  return String(value).replace(/\./g, '').replace(/,/g, '.').replace(/[^\d.-]/g, '');
};

const formatMoney = (value) => `${formatPrice(Math.round(Number(value || 0)), false, '.')}\u00a0đ`;
const normalizeComboUrl = (value) => {
  const rawValue = `${value || ''}`.trim();
  if (!rawValue) return '';

  try {
    const parsedUrl = new URL(/^https?:\/\//i.test(rawValue) ? rawValue : `https://${rawValue}`);
    if (!['http:', 'https:'].includes(parsedUrl.protocol)) return rawValue;
    parsedUrl.hash = '';
    parsedUrl.hostname = parsedUrl.hostname.toLowerCase();
    parsedUrl.pathname = parsedUrl.pathname.replace(/\/+$/, '') || '/';
    return parsedUrl.toString().replace(/\/$/, '');
  } catch {
    return rawValue;
  }
};
const validateComboUrl = (value) => {
  if (!value) return true;
  try {
    const parsedUrl = new URL(normalizeComboUrl(value));
    return ['http:', 'https:'].includes(parsedUrl.protocol) || 'Link web không đúng định dạng.';
  } catch {
    return 'Link web không đúng định dạng.';
  }
};
const normalizeComboCode = (value) =>
  `${value || ''}`
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[đĐ]/g, 'D')
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 255);
const formatProfitRate = (profit, saleAmount) => {
  const saleValue = Number(saleAmount || 0);
  if (!saleValue) return '0%';
  return `${Math.round((Number(profit || 0) / saleValue) * 10000) / 100}%`;
};

const getAppliedPrice = (product) => Number(product?.applied_price_vat || product?.price_vat || product?.price || 0);

const normalizeProductForForm = (product = {}) => {
  const quantity = Number(product?.quantity || 1);
  const appliedPrice = getAppliedPrice(product);
  const listPrice = Number(product?.list_price || appliedPrice || 0);
  const salePrice = Number(product?.sale_price || appliedPrice || 0);
  const costPrice = Number(product?.cost_price || product?.reference_cost_price || 0);

  return {
    ...product,
    quantity,
    list_price: listPrice,
    sale_price: salePrice,
    total_price: Number(product?.total_price || quantity * salePrice || 0),
    cost_price: costPrice,
    total_cost_price: Number(product?.total_cost_price || quantity * costPrice || 0),
    applied_price_vat: appliedPrice,
    applied_base_price: Number(product?.applied_base_price || product?.base_price || 0),
    price_id: product?.price_id || null,
  };
};

const normalizeProductForSubmit = (product, index) => {
  const quantity = Number(product?.quantity || 1);
  const salePrice = Number(product?.sale_price || 0);
  const listPrice = Number(product?.list_price || 0);
  const costPrice = Number(product?.cost_price || 0);

  return {
    ...product,
    product_id: product?.product_id,
    quantity,
    list_price: listPrice,
    sale_price: salePrice,
    total_price: quantity * salePrice,
    cost_price: costPrice,
    total_cost_price: quantity * costPrice,
    is_gift: product?.is_gift ? 1 : 0,
    sort_order: index + 1,
  };
};

const PromotionComboFormPage = () => {
  const { comboId } = useParams();
  const methods = useForm({ defaultValues });
  const { control, handleSubmit, reset, setValue, watch } = methods;
  const { fields, append, remove } = useFieldArray({ control, name: 'products' });
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [comboTypes, setComboTypes] = useState([]);

  const isEdit = Boolean(comboId);
  const watchedProducts = watch('products');
  const currentComboPrice = watch('combo_price');
  const comboPriceId = watch('combo_price_id');
  const products = watchedProducts || [];
  const comboPriceHint = useMemo(() => {
    if (comboPriceId) {
      return `SL_PRICES #${comboPriceId} • Đã duyệt • Đang áp dụng`;
    }
    return 'Chưa có giá đã duyệt trong SL_PRICES';
  }, [comboPriceId]);

  useEffect(() => {
    getComboTypes().then(setComboTypes).catch(() => setComboTypes([]));
  }, []);

  useEffect(() => {
    if (!comboId) {
      reset(defaultValues);
      return;
    }

    setLoading(true);
    getComboDetail(comboId)
      .then((data) => {
        reset({
          ...defaultValues,
          ...data,
          products: (data?.products || []).map(normalizeProductForForm),
        });
      })
      .catch((error) => {
        showToast.error(error?.message || 'Không tải được thông tin combo.');
      })
      .finally(() => setLoading(false));
  }, [comboId, reset]);

  const listPriceTotal = products.reduce(
    (total, product) => total + Number(product?.quantity || 0) * Number(product?.list_price || 0),
    0,
  );
  const salePriceTotal = products.reduce(
    (total, product) => total + Number(product?.quantity || 0) * Number(product?.sale_price || 0),
    0,
  );
  const costPriceTotal = products.reduce(
    (total, product) => total + Number(product?.quantity || 0) * Number(product?.cost_price || 0),
    0,
  );

  const hasCurrentComboPrice = Number(currentComboPrice || 0) > 0;
  const profitBasePrice = hasCurrentComboPrice ? Number(currentComboPrice) : 0;
  const profitAmount = hasCurrentComboPrice ? profitBasePrice - costPriceTotal : 0;
  const productProfitTotal = salePriceTotal - costPriceTotal;

  useEffect(() => {
    setValue('combo_list_price', listPriceTotal);
  }, [listPriceTotal, setValue]);

  const handleAddProduct = () => {
    append({
      product_id: null,
      product_code: '',
      product_name: '',
      unit_id: null,
      unit_name: '',
      quantity: 1,
      list_price: 0,
      sale_price: 0,
      total_price: 0,
      cost_price: 0,
      total_cost_price: 0,
      price_id: null,
      applied_price_vat: 0,
      applied_base_price: 0,
      is_gift: 0,
      note: '',
    });
  };

  const handleClose = () => {
    window._$g.rdr('/promotion-combos');
  };

  const onSubmit = async (values, openPricing = false) => {
    const payloadProducts = (values?.products || []).map(normalizeProductForSubmit);
    if (!payloadProducts.length) {
      showToast.warning('Vui lòng thêm ít nhất một sản phẩm trong combo.');
      return;
    }

    try {
      setSubmitting(true);
      const payload = {
        ...values,
        products: payloadProducts,
      };
      let savedComboId = comboId;
      if (isEdit) {
        const response = await updateCombo(comboId, payload);
        savedComboId = response?.combo_id || comboId;
        showToast.success('Cập nhật combo/Bộ sản phẩm thành công.');
      } else {
        const response = await createCombo(payload);
        savedComboId = response?.combo_id;
        showToast.success('Thêm mới combo/Bộ sản phẩm thành công.');
      }
      if (openPricing && savedComboId) {
        window._$g.rdr(`/prices/combo/add/${savedComboId}`);
      } else {
        handleClose();
      }
    } catch (error) {
      showToast.error(error?.message || 'Có lỗi xảy ra khi lưu combo.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className='bw_main_wrapp'>
      <FormProvider {...methods}>
        <ComboLayout>
          <div>
            <Section>
              <PriceSummary>
                <AppliedPriceCard>
                  <span>Giá combo đang áp dụng</span>
                  <strong>{Number(currentComboPrice) > 0 ? formatMoney(currentComboPrice) : 'Chưa làm giá'}</strong>
                  <small>{comboPriceHint}</small>
                </AppliedPriceCard>
                <PriceCard>
                  <span>Tổng giá vốn</span>
                  <strong>{formatMoney(costPriceTotal)}</strong>
                </PriceCard>
                <PriceCard>
                  <span>Lãi/Lỗ</span>
                  {hasCurrentComboPrice ? (
                    <ProfitValue $value={profitAmount}>{formatMoney(profitAmount)}</ProfitValue>
                  ) : (
                    <strong>Chưa tính</strong>
                  )}
                  <small>
                    {hasCurrentComboPrice
                      ? `Theo giá combo đang áp dụng, biên ${formatProfitRate(profitAmount, profitBasePrice)}`
                      : 'Cần làm giá combo trước khi tính lãi/lỗ'}
                  </small>
                </PriceCard>
              </PriceSummary>
              <InfoGrid>
                <FormItem label='Tên combo/Bộ sản phẩm' isRequired>
                  <FormInput
                    field='combo_name'
                    placeholder='Nhập tên combo/Bộ sản phẩm'
                    validation={{ required: 'Tên combo/Bộ sản phẩm là bắt buộc' }}
                    disabled={loading}
                  />
                </FormItem>
                <FormItem label='Mã combo'>
                  <FormInput
                    field='combo_code'
                    className='bw_combo_code_input'
                    placeholder='Ví dụ: DAN-KARAOKE-BIK-2026-11'
                    disabled={loading}
                    onBlur={(event) => setValue('combo_code', normalizeComboCode(event.target.value), { shouldDirty: true })}
                    onPaste={(event) => {
                      event.preventDefault();
                      setValue('combo_code', normalizeComboCode(event.clipboardData.getData('text')), { shouldDirty: true });
                    }}
                  />
                </FormItem>
                <FormItem label='Nhóm combo'>
                  <FormSelect field='combo_type_id' list={comboTypes} placeholder='--Chọn--' disabled={loading} />
                </FormItem>
                <FormItem label='URL combo'>
                  <FormInput
                    field='url_combo'
                    placeholder='Dán link combo trên website'
                    validation={{ validate: validateComboUrl }}
                    disabled={loading}
                    onBlur={(event) =>
                      setValue('url_combo', normalizeComboUrl(event.target.value), { shouldDirty: true, shouldValidate: true })
                    }
                  />
                </FormItem>
                <FormItem label='Trạng thái'>
                  <FormSelect
                    field='is_active'
                    list={[
                      { value: 1, label: 'Kích hoạt' },
                      { value: 0, label: 'Ẩn' },
                    ]}
                    allowClear={false}
                    disabled={loading}
                  />
                </FormItem>
                <FormItem label='Mô tả'>
                  <FormTextArea field='description' rows={3} placeholder='Nhập mô tả combo' disabled={loading} />
                </FormItem>
              </InfoGrid>
              <QuickGuide>
                <strong>Quy trình nhanh</strong>
                <span>1. Chọn sản phẩm; mọi giá nhập tại form này đều đã gồm VAT.</span>
                <span>2. Giá bán combo chỉ lấy từ mức “Giá combo đang áp dụng” trong bảng giá.</span>
                <span>
                  3. Bấm “{isEdit ? 'Lưu & cập nhật giá' : 'Lưu & làm giá'}” để mở thẳng màn làm giá combo.
                </span>
              </QuickGuide>
            </Section>
          </div>

          <Section>
            <SectionHeader>
              <h3>Thành phần combo</h3>
              <BWButton type='success' icon='fi fi-rr-plus' content='Thêm sản phẩm' onClick={handleAddProduct} />
            </SectionHeader>
            <ProductToolbar>
              <ProductStats>
                <StatPill>
                  Sản phẩm <strong>{fields.length}</strong>
                </StatPill>
                <StatPill>
                  Giá combo đang áp dụng <strong>{hasCurrentComboPrice ? formatMoney(currentComboPrice) : 'Chưa làm giá'}</strong>
                </StatPill>
                <StatPill>
                  Giá vốn <strong>{formatMoney(costPriceTotal)}</strong>
                </StatPill>
                <StatPill>
                  Lãi/Lỗ{' '}
                  {hasCurrentComboPrice ? (
                    <ProfitValue $value={profitAmount}>{formatMoney(profitAmount)}</ProfitValue>
                  ) : (
                    <strong>Chưa tính</strong>
                  )}
                </StatPill>
              </ProductStats>
            </ProductToolbar>
            <ProductTable>
              <table>
                <colgroup>
                  <col style={{ width: 44 }} />
                  <col />
                  <col style={{ width: 50 }} />
                  <col style={{ width: 58 }} />
                  <col style={{ width: '11%' }} />
                  <col style={{ width: '11%' }} />
                  <col style={{ width: '11%' }} />
                  <col style={{ width: '9%' }} />
                  <col style={{ width: 46 }} />
                  <col style={{ width: '9%' }} />
                  <col style={{ width: 48 }} />
                </colgroup>
                <thead>
                  <tr>
                    <th style={{ textAlign: 'center' }}>STT</th>
                    <th>Sản phẩm</th>
                    <th>ĐVT</th>
                    <th>SL</th>
                    <th title='Giá niêm yết sản phẩm đã gồm VAT'>Giá niêm yết SP (đã VAT)</th>
                    <th title='Giá phân bổ đã gồm VAT, chỉ dùng tham chiếu thành phần'>Giá phân bổ (đã VAT)</th>
                    <th>Giá vốn</th>
                    <th>Lãi/Lỗ</th>
                    <th style={{ textAlign: 'center' }}>Quà</th>
                    <th>Ghi chú</th>
                    <th style={{ textAlign: 'center' }}>Xóa</th>
                  </tr>
                </thead>
                <tbody>
                  {fields.map((field, index) => {
                    const appliedPrice = Number(watch(`products.${index}.applied_price_vat`) || 0);
                    const quantity = Number(watch(`products.${index}.quantity`) || 0);
                    const salePrice = Number(watch(`products.${index}.sale_price`) || 0);
                    const costPrice = Number(watch(`products.${index}.cost_price`) || 0);
                    const rowListAmount = quantity * Number(watch(`products.${index}.list_price`) || 0);
                    const rowSaleAmount = quantity * salePrice;
                    const rowCostAmount = quantity * costPrice;
                    const rowProfit = rowSaleAmount - rowCostAmount;
                    return (
                      <tr key={field.id}>
                        <td style={{ textAlign: 'center' }}>{index + 1}</td>
                        <td>
                          <FormDebouneSelect
                            field={`products.${index}.product_id`}
                            placeholder='Tìm mã hoặc tên sản phẩm'
                            fetchOptions={(search) => getComboProductOptions({ search })}
                            onChange={(_, option) => {
                              const normalizedProduct = normalizeProductForForm(option);
                              setValue(`products.${index}.product_code`, option?.product_code || '');
                              setValue(`products.${index}.product_name`, option?.product_name || option?.label || '');
                              setValue(`products.${index}.unit_id`, option?.unit_id || null);
                              setValue(`products.${index}.unit_name`, option?.unit_name || '');
                              setValue(`products.${index}.price_id`, normalizedProduct.price_id);
                              setValue(`products.${index}.applied_price_vat`, normalizedProduct.applied_price_vat);
                              setValue(`products.${index}.applied_base_price`, normalizedProduct.applied_base_price);
                              setValue(`products.${index}.list_price`, normalizedProduct.list_price, { shouldDirty: true });
                              setValue(`products.${index}.sale_price`, normalizedProduct.sale_price, { shouldDirty: true });
                              setValue(`products.${index}.cost_price`, normalizedProduct.cost_price, { shouldDirty: true });
                            }}
                          />
                          <ProductSubText $hasPrice={appliedPrice > 0}>
                            {appliedPrice > 0
                              ? `Đã lấy giá combo: ${formatMoney(appliedPrice)}`
                              : 'Chưa có giá đang áp dụng'}
                          </ProductSubText>
                        </td>
                        <td>
                          <div className='bw_combo_inline_control'>{watch(`products.${index}.unit_name`) || '-'}</div>
                        </td>
                        <td>
                          <FormNumber field={`products.${index}.quantity`} min={0.0001} />
                        </td>
                        <td>
                          <PriceCell>
                            <FormNumber
                              field={`products.${index}.list_price`}
                              min={0}
                              formatter={moneyFormatter}
                              parser={moneyParser}
                            />
                            <small>Tổng: {formatMoney(rowListAmount)}</small>
                          </PriceCell>
                        </td>
                        <td>
                          <PriceCell>
                            <FormNumber
                              field={`products.${index}.sale_price`}
                              min={0}
                              formatter={moneyFormatter}
                              parser={moneyParser}
                            />
                            <small>Tổng: {formatMoney(rowSaleAmount)}</small>
                          </PriceCell>
                        </td>
                        <td>
                          <PriceCell>
                            <FormNumber
                              field={`products.${index}.cost_price`}
                              min={0}
                              formatter={moneyFormatter}
                              parser={moneyParser}
                            />
                            <small>Tổng: {formatMoney(rowCostAmount)}</small>
                          </PriceCell>
                        </td>
                        <td>
                          <div className='bw_combo_inline_control'>
                            <ProfitValue $value={rowProfit}>{formatMoney(rowProfit)}</ProfitValue>
                          </div>
                          <small style={{ display: 'block', color: '#667085', marginTop: 3 }}>
                            {formatProfitRate(rowProfit, rowSaleAmount)}
                          </small>
                        </td>
                        <td style={{ textAlign: 'center' }}>
                          <div className='bw_combo_inline_control bw_combo_inline_control_center'>
                            <label className='bw_checkbox bw_combo_gift_checkbox'>
                              <input
                                type='checkbox'
                                checked={Boolean(watch(`products.${index}.is_gift`))}
                                onChange={(event) => setValue(`products.${index}.is_gift`, event.target.checked ? 1 : 0)}
                              />
                              <span />
                            </label>
                          </div>
                        </td>
                        <td>
                          <FormInput field={`products.${index}.note`} className='bw_inp' placeholder='Ghi chú' />
                        </td>
                        <td style={{ textAlign: 'center' }}>
                          <div className='bw_combo_inline_control bw_combo_inline_control_center'>
                            <button
                              type='button'
                              className='bw_btn_table bw_red'
                              title='Xóa sản phẩm'
                              onClick={() => remove(index)}>
                              <i className='fi fi-rr-trash' />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
                {fields.length ? (
                  <tfoot>
                    <tr>
                      <td colSpan={4} className='bw_combo_total_label'>Tổng cộng</td>
                      <td className='bw_combo_total_value'>{formatMoney(listPriceTotal)}</td>
                      <td className='bw_combo_total_value'>{formatMoney(salePriceTotal)}</td>
                      <td className='bw_combo_total_value'>{formatMoney(costPriceTotal)}</td>
                      <td className='bw_combo_total_value'>{formatMoney(productProfitTotal)}</td>
                      <td colSpan={3} />
                    </tr>
                  </tfoot>
                ) : null}
              </table>
              {!fields.length ? <EmptyProducts>Chưa có sản phẩm trong combo</EmptyProducts> : null}
            </ProductTable>
          </Section>
        </ComboLayout>

        <PageActions>
          <BWButton
            type='success'
            icon='fi fi-rr-check'
            content={isEdit ? 'Lưu combo' : 'Lưu combo'}
            loading={submitting}
            onClick={handleSubmit((values) => onSubmit(values, false))}
          />
          <BWButton
            type='primary'
            icon='fi fi-rr-tags'
            content={isEdit ? 'Lưu & cập nhật giá' : 'Lưu & làm giá'}
            loading={submitting}
            onClick={handleSubmit((values) => onSubmit(values, true))}
          />
          <BWButton outline type='primary' icon='fi fi-rr-cross' content='Đóng' onClick={handleClose} />
        </PageActions>
      </FormProvider>
    </div>
  );
};

export default PromotionComboFormPage;
